/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
type Validator = (value: string) => boolean;

interface BaseGrammar {
    key: string; // 可以使用的key,例如名字，英文状态下不区分字符大小写
    value?: string | number; // 对应的实际发送到后端的key 例如 Name,不传递时候默认与key一样
    allowSymbol: string[]; // 定义合法的Symbol列表，例如 ['=','!=']
    map?: string[]; // 定义如何映射Symbol,例如Name中的 = 映射为实际搜索条件中 :,则参数传递为['=',':']。数组必须为偶数项。
    $default?: string; // 定义默认的Symbol，即当为空格分割时候的symbol，例如 LUN Name 001 等价于 LUN NAME=001;
    api?: string;
    tip?: {
        style?: any; // 注入样式
        value: string; // 显示的值
    } | string; // 输入该字段时候出现其后的字段，只会在key,symbol完整，输入value时候出现
}

interface StringGrammar extends BaseGrammar {
    type: 'string';
    validator?: Validator;
}

interface BoolGrammar extends BaseGrammar {
    type: 'boolean';
    enumValue?: {
        [props: string]: string;
    };
}

interface EnumGrammar extends BaseGrammar {
    type: 'enum';
    enumValue: {
        [props: string]: string;
    };
}

interface NumGrammar extends BaseGrammar {
    type: 'number';
    validator?: Validator;
    format?: (value: any) => string | number;
    unit?: string;
}

interface DateGrammar extends BaseGrammar {
    type: 'date';
    range?: [Date, Date];
}

interface PerGrammar extends BaseGrammar {
    type: 'performance';
    value: number;
    enumValue?: {
        [props: string]: string;
    };
}

export type Grammar = StringGrammar | BoolGrammar | EnumGrammar | NumGrammar | DateGrammar | PerGrammar;

export interface GrammarList {
    [prop: string]: {
        [prop: string]: Grammar[];
    };
}

export function isStringGrammar(value: { type: string }): value is StringGrammar {
    return value.type === 'string';
}

export function isBooleanGrammar(value: { type: string }): value is BoolGrammar {
    return value.type === 'boolean';
}

export function isEnumGrammar(value: { type: string }): value is EnumGrammar {
    return value.type === 'enum';
}

export function isNumGrammar(value: { type: string }): value is NumGrammar {
    return value.type === 'number';
}

export function isDateGrammar(value: { type: string }): value is DateGrammar {
    return value.type === 'date';
}

export function isPerGrammar(value: BoolGrammar | EnumGrammar | PerGrammar): value is PerGrammar {
    return value.type === 'performance';
}

export interface Condition {
    hyphen?: '||' | '&&';
    key: string;
    symbol: string;
    value: any;
}

export enum TokenType {
    Symbol = 'Symbol',
    Chinese = 'Chinese',
    Alphabet = 'Alphabet',
    Number = 'Number',
    Time = 'Time',
    Hyphen = 'Hyphen',
    String = 'String'
}

export const $$default = 'DEFAULT_SYMBOL';
